import { AddOutlined, RemoveOutlined } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@mui/material";
import { addMinutes, differenceInMilliseconds, format, subMinutes } from "date-fns";
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import colors from "../../../../../config/colors";
import {
  SHIFT_ARRIVE_LATE_CODE,
  SHIFT_CANCEL_CODE,
  SHIFT_LEAVE_EARLY_CODE
} from "../../../../../features/web-worker-app/config/constants";
import { generateDurationOptionsByMinutes } from "../../../../../features/web-worker-app/LeaveRequestPage";
import { getHoursAndMinutesStringFromFloat } from "../../../../../features/web-worker-app/util/helper";
import useWorkerSchedulerData from "../../../../full-time-worker-scheduler/controllers/use-worker-scheduler-data";
import MarkCalledOffSkeleton
  from "./mark-called-off-skeleton";

const isValidDate = (d: any): d is Date => d instanceof Date && !isNaN(d.getTime());

interface CallOffSectionProps {
  workerId: string;
  fromStatus?: string;
  setCalledOffData: Dispatch<SetStateAction<{
    ftRequestTypeId: number;
    ftLeaveTypes: {
      leaveTypeId: number;
      requestedHours: number;
    }[];
    ftSupervisorComment: string;
    ftStartDateTime: string;
    ftEndDateTime: string;
    ftFromStatus: string;
  } | null>>,
  shiftDetails: any;
  initialData?: any;
  setIsDirty?: (isDirty: boolean) => void;
}

const MarkCalledOff = ({
  workerId,
  fromStatus = "SCH",
  shiftDetails,
  setCalledOffData,
  initialData,
  setIsDirty,
}: CallOffSectionProps) => {
  const { t } = useTranslation();
  const shiftStart = shiftDetails?.startDateTime;
  const shiftEnd = shiftDetails?.endDateTime;
  const {
    requestTypes,
    fetchRequestTypesStatus,
    fetchWorkerCallOffsStatus,
    workerCallOffs,
  } = useWorkerSchedulerData();
  const [selectedLeaveType, setSelectedLeaveType] = useState<
    | {
        id: number;
        requestedHours: number;
        incrementHours: number;
        pairingEnabled: boolean;
        availableHrs: number | null;
        isNoLimit: boolean;
      }
    | undefined
  >();
  const [duration, setDuration] = useState<number | null>(0);
  const [duration2, setDuration2] = useState<number | null>(0);
  const [requestTypeId, setRequestTypeId] = useState<number | undefined>(undefined);
  const [durationOptions, setDurationOptions] = useState<{ text: string; timeInMinutes: number; }[]>([]);
  const [durationOptions2, setDurationOptions2] = useState<{ text: string; timeInMinutes: number; }[]>([]);
  const [isPairingAccordionOpen, setIsPairingAccordionOpen] = useState(false);
  const [selectedLeaveType2, setSelectedLeaveType2] = useState<{
    id: number;
    requestedHours: number;
    incrementHours: number;
    pairingEnabled: boolean;
    availableHrs: number | null;
    isNoLimit: boolean;
  } | undefined>();
  const [callOffDataValue, setCallOffDataValue] = useState<
    {
      requestTypeId: number;
      supervisorComment: string;
      startDateTime: string;
      endDateTime: string;
      leaveTypes: {
        leaveTypeId: number;
        requestedHours: number;
      }[]
    }
  >({
    requestTypeId: 0,
    startDateTime: shiftStart,
    endDateTime: shiftEnd,
    supervisorComment: "",
    leaveTypes: [],
  });
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const lastSelectedLeaveTypeId = React.useRef<number | undefined>(undefined);

  // NEW memoized value for the total shift duration in minutes
  const shiftDurationInMinutes = useMemo(() => {
    if (!shiftStart || !shiftEnd) return 0;
    return differenceInMilliseconds(new Date(shiftEnd), new Date(shiftStart))/(1000*60);
  }, [shiftStart, shiftEnd]);
  const type = useMemo(() => {
    return requestTypes?.find((type) => type.id === callOffDataValue?.requestTypeId)
      ?.code.toLowerCase();
  },[callOffDataValue.requestTypeId, requestTypes]);
  const isItemDisabled = useCallback((item: { incrementHours: number }) => {
    let isDisabled = false;

    // THIS IS THE CRITICAL FIX: Only apply logic for Arrive Late / Leave Early
    const isPartialDayRequest =
      type === SHIFT_ARRIVE_LATE_CODE || type === SHIFT_LEAVE_EARLY_CODE;

    if (isPartialDayRequest) {
      const minIncrementMinutes = Math.round(item.incrementHours * 60);
      const remainingMinutes = shiftDurationInMinutes - (duration ?? 0);

      if (remainingMinutes < minIncrementMinutes) {
        isDisabled = true;
      }
    }

    return isDisabled;
  },[duration, shiftDurationInMinutes, type]);

  const isPrimaryItemDisabled = useCallback((item: { incrementHours: number }) => {
    let isDisabled = false;

    const isParialDayRequest =
      type === SHIFT_ARRIVE_LATE_CODE || type === SHIFT_LEAVE_EARLY_CODE;
    if (isParialDayRequest) {
      const minIncrementMinutes = Math.round(item.incrementHours * 60);
      const remainingMinutes = shiftDurationInMinutes - minIncrementMinutes;
      if (remainingMinutes < 1) {
        isDisabled = true;
      }
    }

    return isDisabled;

  },[shiftDurationInMinutes, type]);

  useEffect(() => {
    // If we are in "Create" mode, we are initialized once request types are available
    if (!initialData) {
      if (requestTypes.length > 0) {
        setIsDataInitialized(true);
      }
      return;
    }

    // Ensure metadata is loaded before trying to map initialData
    if (!isDataInitialized && initialData && requestTypes.length > 0 && workerCallOffs.length > 0) {
      const existingReqType = requestTypes.find(t => t.id === initialData.requestTypeId);

      // Set the internal ID state so the RadioGroup reflects the correct type (sc, al, le)
      if (existingReqType) {
        setRequestTypeId(existingReqType.id);
      }

      setCallOffDataValue({
        requestTypeId: initialData.requestTypeId,
        supervisorComment: initialData.supervisorComment || "",
        startDateTime: initialData.startDate,
        endDateTime: initialData.endDate,
        leaveTypes: initialData.leaveTypes.map(
          (lt: { leaveTypeId: number; requestedHours: number }) => ({
            leaveTypeId: lt.leaveTypeId,
            requestedHours: lt.requestedHours,
          })
        ),
      });

      // Handle Primary Leave Type
      const firstLeave = initialData.leaveTypes[0];
      const foundPrimary = workerCallOffs.find(w => w.id === firstLeave.leaveTypeId);

      if (foundPrimary) {
        setSelectedLeaveType(foundPrimary);
        setDuration(Math.round(firstLeave.requestedHours * 60));
      }

      // Handle Secondary Leave Type (Pairing)
      if (initialData.leaveTypes.length > 1) {
        const secondLeave = initialData.leaveTypes[1];
        const foundSecondary = workerCallOffs.find(w => w.id === secondLeave.leaveTypeId);
        if (foundSecondary) {
          setIsPairingAccordionOpen(true);
          setTimeout(() => {
            setSelectedLeaveType2(prevState => {if (prevState !== foundSecondary) {
              return foundSecondary;
            } else {return prevState;}});
          },10);
          setTimeout(() => {
            setDuration2(prevState => {
              const roundedDuration = Math.round(secondLeave.requestedHours * 60);
              if (prevState !== roundedDuration) {
                return roundedDuration;
              } else {
                return prevState;
              }
            });
          },50);
        }
      }

      // Signal that mapping is finished
      setTimeout(() => setIsDataInitialized(true), 250);
    }
  }, [initialData, isDataInitialized, requestTypes, workerCallOffs]);

  useEffect(() => {
    try {
      if (selectedLeaveType && shiftStart && shiftEnd) {
        // If we are initializing from initialData, don't reset secondary
        if (isDataInitialized && !initialData && lastSelectedLeaveTypeId.current !== selectedLeaveType.id) {
          // Reset secondary selection when primary changes
          setSelectedLeaveType2(undefined);
          setDuration2(0);
        }
        lastSelectedLeaveTypeId.current = selectedLeaveType.id;

        if (type === SHIFT_CANCEL_CODE) {
          const correctedIncrementHour = selectedLeaveType.incrementHours > shiftDurationInMinutes/60 ? shiftDurationInMinutes/60 : selectedLeaveType.incrementHours;
          // Case A: Full shift. Max duration IS the full shift.
          const options = generateDurationOptionsByMinutes(shiftDurationInMinutes, correctedIncrementHour.toString());
          const lastOption = options.length > 0 ? options[options.length - 1].timeInMinutes : shiftDurationInMinutes;
          if (lastOption < shiftDurationInMinutes) {
            options.push({
              text: getHoursAndMinutesStringFromFloat(shiftDurationInMinutes/60),
              timeInMinutes: shiftDurationInMinutes,
            });
            setDurationOptions(options);
          } else {
            setDurationOptions(options);
          }
          // For full shift, the duration is always the max
          setDuration(shiftDurationInMinutes);
          setCallOffDataValue((prev) => ({
            ...prev,
            leaveTypes: [{
              leaveTypeId: Number(selectedLeaveType?.id),
              requestedHours: shiftDurationInMinutes/60 || 0,
            }],
            startDateTime: shiftStart,
            endDateTime: shiftEnd,
          }));
        } else {
          // Case B: Partial shift. Max duration MUST BE LESS than the full shift.
          const smallestIncrement = Math.round(selectedLeaveType.incrementHours * 60);
          const maxSelectableDuration = shiftDurationInMinutes - smallestIncrement;

          const options = generateDurationOptionsByMinutes(
            maxSelectableDuration > 0 ? maxSelectableDuration : 0,
            selectedLeaveType.incrementHours.toString()
          );

          // If the current duration is not in options, but it's less than or equal to maxSelectableDuration, add it
          const currentDuration = duration ?? 0;
          const isCurrentDurationInOptions = options.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration));
          if (!isCurrentDurationInOptions && currentDuration > 0 && currentDuration <= maxSelectableDuration) {
            options.push({
              text: getHoursAndMinutesStringFromFloat(currentDuration / 60),
              timeInMinutes: currentDuration
            });
            options.sort((a, b) => a.timeInMinutes - b.timeInMinutes);
          }
          setDurationOptions(options);

          // --- FIX: Preserving duration if it's still valid ---
          const isCurrentDurationValid = options.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration));
          
          // During initialization, if we have initialData, we MUST NOT reset the duration 
          // to options[0] just because options might not have loaded yet.
          let newDuration = currentDuration;
          if (isDataInitialized || !initialData) {
            newDuration = isCurrentDurationValid ? currentDuration : (options[0]?.timeInMinutes ?? 0);
          } else if (initialData && !isDataInitialized) {
             // If we are initializing and have initialData, ensure we use the duration from it
             const firstLeave = initialData.leaveTypes[0];
             if (firstLeave) {
                newDuration = Math.round(firstLeave.requestedHours * 60);
             }
          }

          setDuration(newDuration);
          if (isValidDate(new Date(shiftStart))
            && isValidDate(new Date(shiftEnd))) {
            setCallOffDataValue((prev) => ({
              ...prev,
              leaveTypes: [
                {
                  leaveTypeId: Number(selectedLeaveType?.id),
                  requestedHours: newDuration / 60 || 0,
                },
              ],
              startDateTime:
                type === SHIFT_LEAVE_EARLY_CODE &&
                isValidDate(new Date(shiftEnd)) &&
                isValidDate(
                  subMinutes(
                    new Date(shiftEnd),
                    Number(newDuration ?? 0)
                  )
                )
                  ? subMinutes(
                      new Date(shiftEnd),
                      Number(newDuration ?? 0)
                    )?.toISOString()
                  : shiftStart,
              endDateTime:
                type === SHIFT_ARRIVE_LATE_CODE &&
                isValidDate(new Date(shiftStart)) &&
                isValidDate(
                  addMinutes(
                    new Date(shiftStart),
                    Number(newDuration ?? 0)
                  )
                )
                  ? addMinutes(
                      new Date(shiftStart),
                      Number(newDuration ?? 0)
                    )?.toISOString()
                  : shiftEnd,
            }));
          }
          if (options.length === 0) {
            setDuration(0); // Reset if there are no valid options
            setCallOffDataValue((prev) => ({
              ...prev,
              leaveTypes: [],
              startDateTime: shiftStart,
              endDateTime: shiftEnd,
            }));
          }
        }
      }
    } catch (e) {
      // Do nothing
    }
  }, [duration, initialData, isDataInitialized, selectedLeaveType, shiftStart, shiftEnd, shiftDurationInMinutes, type]);

  useEffect(() => {
    let newPrimaryOptions: {
      text: string,
      timeInMinutes: number,
    }[] = [];
    let newPrimaryDuration: number | null = 0;
    if (selectedLeaveType && selectedLeaveType2 && shiftStart && shiftEnd) {
      const minSecondaryIncrement = Math.round((selectedLeaveType2?.incrementHours ?? 1) * 60);
      let maxPrimaryDuration;
      if (type === SHIFT_CANCEL_CODE) {
        // For a Full Shift, the max is total shift minus the minimum for the first type.
        const minPrimaryIncrement = Math.round((selectedLeaveType?.incrementHours ?? 1) * 60);
        maxPrimaryDuration = shiftDurationInMinutes - minPrimaryIncrement;
        maxPrimaryDuration = maxPrimaryDuration < 0 ? shiftDurationInMinutes : maxPrimaryDuration;
        const correctedIncrementHour = (selectedLeaveType?.incrementHours ?? 1) > shiftDurationInMinutes/60 ? shiftDurationInMinutes/60 : (selectedLeaveType?.incrementHours ?? 1);
        newPrimaryOptions = generateDurationOptionsByMinutes(
          maxPrimaryDuration >= 0 ? maxPrimaryDuration : 0,
          correctedIncrementHour.toString()
        );
        const lastOption = newPrimaryOptions.length > 0 ? newPrimaryOptions[newPrimaryOptions.length - 1].timeInMinutes : maxPrimaryDuration;
        if (lastOption < maxPrimaryDuration && newPrimaryOptions.length === 0) {
          newPrimaryOptions.push({
            text: getHoursAndMinutesStringFromFloat(maxPrimaryDuration/60),
            timeInMinutes: maxPrimaryDuration,
          });
        }
        
        // If the current duration is not in options, but it's less than or equal to maxPrimaryDuration, add it
        const currentDuration = duration ?? 0;
        const isCurrentDurationInOptions = newPrimaryOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration));
        if (!isCurrentDurationInOptions && currentDuration > 0 && currentDuration <= maxPrimaryDuration) {
          newPrimaryOptions.push({
            text: getHoursAndMinutesStringFromFloat(currentDuration / 60),
            timeInMinutes: currentDuration
          });
          newPrimaryOptions.sort((a, b) => a.timeInMinutes - b.timeInMinutes);
        }

        const isCurrentDurationValid = newPrimaryOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration));
        
        if (isDataInitialized || !initialData) {
          newPrimaryDuration = isCurrentDurationValid ? currentDuration : (newPrimaryOptions?.[newPrimaryOptions.length - 1]?.timeInMinutes ?? null);
        } else if (initialData && !isDataInitialized) {
          const firstLeave = initialData.leaveTypes[0];
          if (firstLeave) {
            newPrimaryDuration = Math.round(firstLeave.requestedHours * 60);
          } else {
            newPrimaryDuration = currentDuration;
          }
        } else {
          newPrimaryDuration = currentDuration;
        }
      } else {
        // For Partial Day, the max is total shift minus minimum for the second type, minus 1.
        // This ensures the total can never equal the full shift duration.
        maxPrimaryDuration = shiftDurationInMinutes - minSecondaryIncrement - 1;
        newPrimaryOptions = generateDurationOptionsByMinutes(
          maxPrimaryDuration >= 0 ? maxPrimaryDuration : 0,
          selectedLeaveType?.incrementHours.toString() ?? "1"
        );

        // If the current duration is not in options, but it's less than or equal to maxPrimaryDuration, add it
        const currentDuration = duration ?? 0;
        const isCurrentDurationInOptions = newPrimaryOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration));
        if (!isCurrentDurationInOptions && currentDuration > 0 && currentDuration <= maxPrimaryDuration) {
          newPrimaryOptions.push({
            text: getHoursAndMinutesStringFromFloat(currentDuration / 60),
            timeInMinutes: currentDuration
          });
          newPrimaryOptions.sort((a, b) => a.timeInMinutes - b.timeInMinutes);
        }

        const isCurrentDurationValid = newPrimaryOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration));
        
        if (isDataInitialized || !initialData) {
          newPrimaryDuration = isCurrentDurationValid ? currentDuration : (newPrimaryOptions?.[0]?.timeInMinutes ?? null);
        } else if (initialData && !isDataInitialized) {
          const firstLeave = initialData.leaveTypes[0];
          if (firstLeave) {
            newPrimaryDuration = Math.round(firstLeave.requestedHours * 60);
          } else {
            newPrimaryDuration = currentDuration;
          }
        } else {
          newPrimaryDuration = currentDuration;
        }
      }

      if (newPrimaryOptions.length > 0) {
        // Now, use this new, known `newPrimaryDuration` for all subsequent calculations.
        if (type === SHIFT_CANCEL_CODE) {
          setDuration2(shiftDurationInMinutes - (newPrimaryDuration ?? 0));
          setDurationOptions(newPrimaryOptions);
          setDuration(newPrimaryDuration);
          const firstDuration = newPrimaryDuration ?? 0;
          const secondDuration = shiftDurationInMinutes - (newPrimaryDuration ?? 0);
          setCallOffDataValue((prev) => ({
            ...prev,
            leaveTypes: [
              { leaveTypeId: selectedLeaveType.id,
                requestedHours: firstDuration/60,
              },{
                leaveTypeId: selectedLeaveType2.id,
                requestedHours: secondDuration/60,
              }],
            startDateTime: shiftStart,
            endDateTime: shiftEnd
          }));
        } else {
          const remainingMinutes = shiftDurationInMinutes - (newPrimaryDuration ?? 0);
          const newSecondaryOptions = generateDurationOptionsByMinutes(remainingMinutes, selectedLeaveType2.incrementHours.toString());
          
          // If the current duration2 is not in options, but it's less than or equal to remainingMinutes, add it
          const currentDuration2 = duration2 ?? 0;
          const isCurrentDuration2InOptions = newSecondaryOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration2));
          if (!isCurrentDuration2InOptions && currentDuration2 > 0 && currentDuration2 <= remainingMinutes) {
            newSecondaryOptions.push({
              text: getHoursAndMinutesStringFromFloat(currentDuration2 / 60),
              timeInMinutes: currentDuration2
            });
            newSecondaryOptions.sort((a, b) => a.timeInMinutes - b.timeInMinutes);
          }
          setDurationOptions2(newSecondaryOptions);

          // Preserving duration2 if it's still valid
          const isCurrentDuration2Valid = newSecondaryOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration2));
        
          let finalDuration2 = currentDuration2;
          if (type === SHIFT_CANCEL_CODE) {
             finalDuration2 = shiftDurationInMinutes - (newPrimaryDuration ?? 0);
          } else {
             finalDuration2 = isCurrentDuration2Valid ? currentDuration2 : (newSecondaryOptions?.[0]?.timeInMinutes ?? 0);
          }

          setDuration2(finalDuration2);
          setDurationOptions(newPrimaryOptions);
          setDuration(newPrimaryDuration);
          const firstDuration = newPrimaryDuration ?? 0;
          const secondDuration = finalDuration2;
          const totalDuration = type === SHIFT_CANCEL_CODE ? shiftDurationInMinutes : (firstDuration + secondDuration);
          if (isValidDate(new Date(shiftStart))
            && isValidDate(new Date(shiftEnd))) {
            setCallOffDataValue((prev) => ({
              ...prev,
              leaveTypes: [
                {
                  leaveTypeId: selectedLeaveType.id,
                  requestedHours: firstDuration/60,
                }
                ,{
                  leaveTypeId: selectedLeaveType2.id,
                  requestedHours: secondDuration/60,
                }],
              startDateTime: type === SHIFT_LEAVE_EARLY_CODE
              && isValidDate(new Date(shiftEnd))
                && isValidDate(subMinutes(new Date(shiftEnd), totalDuration))
                ?
                subMinutes(new Date(shiftEnd), totalDuration)?.toISOString()
                : shiftStart,
              endDateTime: type === SHIFT_ARRIVE_LATE_CODE
              && isValidDate(new Date(shiftStart))
                && isValidDate(addMinutes(new Date(shiftStart), totalDuration))
                ?
                addMinutes(new Date(shiftStart), totalDuration)?.toISOString()
                : shiftEnd,
            }));
          }
        }
      }
    }
    else if (!selectedLeaveType2 && selectedLeaveType && shiftStart && shiftEnd) {
      // Logic to restore primary options when secondary is deselected/reset
      if (type === SHIFT_CANCEL_CODE) {
        // SPECIAL CASE: For full shift callouts, when pairing is removed,
        // reset the primary duration to the full shift duration.
        const correctedIncrementHour = selectedLeaveType.incrementHours > shiftDurationInMinutes/60 ? shiftDurationInMinutes/60 : selectedLeaveType.incrementHours;
        const options = generateDurationOptionsByMinutes(shiftDurationInMinutes, correctedIncrementHour.toString());
        const lastOption = options.length > 0 ? options[options.length - 1].timeInMinutes : shiftDurationInMinutes;
        if (lastOption !== shiftDurationInMinutes) {
          options.push({
            text: getHoursAndMinutesStringFromFloat(shiftDurationInMinutes/60),
            timeInMinutes: shiftDurationInMinutes,
          });
        }
        setDurationOptions(options);
        setDuration(options?.[options.length - 1]?.timeInMinutes ?? 0);
        setDuration2(0);
        setDurationOptions2([]);
        setCallOffDataValue((prev) => ({
          ...prev,
          leaveTypes: [
            { leaveTypeId: selectedLeaveType.id,
              requestedHours: options?.[options.length - 1]?.timeInMinutes/60,
            }],
          startDateTime: shiftStart,
          endDateTime: shiftEnd
        }));
      } else {
        // For partial day requests, just restore the full list of duration options
        const smallestIncrement = Math.round(selectedLeaveType.incrementHours * 60);
        const maxSelectableDuration = shiftDurationInMinutes - smallestIncrement;
        const fullOptions = generateDurationOptionsByMinutes(
          maxSelectableDuration > 0 ? maxSelectableDuration : 0,
          selectedLeaveType?.incrementHours.toString() ?? "1"
        );
        
        // If the current duration is not in options, but it's less than or equal to maxSelectableDuration, add it
        const currentDuration = duration ?? 0;
        const isCurrentDurationInOptions = fullOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration));
        if (!isCurrentDurationInOptions && currentDuration > 0 && currentDuration <= maxSelectableDuration) {
          fullOptions.push({
            text: getHoursAndMinutesStringFromFloat(currentDuration / 60),
            timeInMinutes: currentDuration
          });
          fullOptions.sort((a, b) => a.timeInMinutes - b.timeInMinutes);
        }
        setDurationOptions(fullOptions);

        // Preserving duration if it's still valid
        const isCurrentDurationValid = fullOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration));
        
        let finalDuration = currentDuration;
        if (isDataInitialized || !initialData) {
          finalDuration = isCurrentDurationValid ? currentDuration : (fullOptions?.[0]?.timeInMinutes ?? 0);
        } else if (initialData && !isDataInitialized) {
          const firstLeave = initialData.leaveTypes[0];
          if (firstLeave) {
            finalDuration = Math.round(firstLeave.requestedHours * 60);
          }
        }

        setDuration(finalDuration);
        setDuration2(0);
        setDurationOptions2([]);
        if (isValidDate(new Date(shiftStart))
          && isValidDate(new Date(shiftEnd))) {
          setCallOffDataValue((prev) => ({
            ...prev,
            leaveTypes: [
              {
                leaveTypeId: selectedLeaveType.id,
                requestedHours: finalDuration / 60,
              }],
            startDateTime: type === SHIFT_LEAVE_EARLY_CODE
            && isValidDate(new Date(shiftEnd))
              && isValidDate(subMinutes(new Date(shiftEnd), Number(finalDuration ?? 0)))
              ?
              subMinutes(new Date(shiftEnd), Number(finalDuration ?? 0))?.toISOString()
              : shiftStart,
            endDateTime: type === SHIFT_ARRIVE_LATE_CODE
            && isValidDate(new Date(shiftStart))
              && isValidDate(addMinutes(new Date(shiftStart), Number(finalDuration ?? 0)))
              ?
              addMinutes(new Date(shiftStart), Number(finalDuration ?? 0))?.toISOString()
              : shiftEnd,
          }));
        }
      }
    }
  }, [duration, duration2, initialData, isDataInitialized, selectedLeaveType, selectedLeaveType2, shiftStart, shiftEnd, shiftDurationInMinutes, type]);

  // NEW: Logic for when PRIMARY duration changes, update secondary options
  useEffect(() => {
    if (selectedLeaveType2 && type !== SHIFT_CANCEL_CODE) {
      const remainingMinutes = shiftDurationInMinutes - (duration ?? 0);
      // The maximum duration for the secondary choice must be less than what remains
      const maxSecondaryDuration = remainingMinutes - 1;

      // Generate the new list of valid options for the secondary dropdown
      const newSecondaryOptions = generateDurationOptionsByMinutes(
        maxSecondaryDuration >= 0 ? maxSecondaryDuration : 0,
        selectedLeaveType2.incrementHours.toString()
      );

      // If the current duration2 is not in options, but it's less than or equal to maxSecondaryDuration, add it
      const currentDuration2 = duration2 ?? 0;
      const isCurrentDuration2InOptions = newSecondaryOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration2));
      if (!isCurrentDuration2InOptions && currentDuration2 > 0 && currentDuration2 <= maxSecondaryDuration) {
        newSecondaryOptions.push({
          text: getHoursAndMinutesStringFromFloat(currentDuration2 / 60),
          timeInMinutes: currentDuration2
        });
        newSecondaryOptions.sort((a, b) => a.timeInMinutes - b.timeInMinutes);
      }
      setDurationOptions2(newSecondaryOptions);

      // Preserving duration2 if it's still valid
      const isCurrentDuration2Valid = newSecondaryOptions.some(opt => Math.round(opt.timeInMinutes) === Math.round(currentDuration2));
      
      let finalDuration2 = currentDuration2;
      if (type === SHIFT_CANCEL_CODE) {
        finalDuration2 = shiftDurationInMinutes - (duration ?? 0);
      } else {
        finalDuration2 = isCurrentDuration2Valid ? currentDuration2 : (newSecondaryOptions?.[0]?.timeInMinutes ?? 0);
      }

      setDuration2(finalDuration2);

      if (isValidDate(new Date(shiftStart))
        && isValidDate(new Date(shiftEnd))) {
        setCallOffDataValue((prev) => {
          const updatedLeaveTypes = [...prev.leaveTypes];
          if (updatedLeaveTypes.length > 1) {
            updatedLeaveTypes[1] = {
              ...updatedLeaveTypes[1],
              requestedHours: finalDuration2 / 60,
            };
          } else if (selectedLeaveType2) {
            updatedLeaveTypes[1] = {
              leaveTypeId: selectedLeaveType2.id,
              requestedHours: finalDuration2 / 60,
            };
          }

          const totalDuration = (updatedLeaveTypes[0]?.requestedHours * 60 ?? 0) + (finalDuration2 ?? 0);

          return {
            ...prev,
            leaveTypes: updatedLeaveTypes,
            startDateTime:
              type === SHIFT_LEAVE_EARLY_CODE &&
              isValidDate(new Date(shiftEnd)) &&
              isValidDate(
                subMinutes(
                  new Date(shiftEnd),
                  totalDuration
                )
              )
                ? subMinutes(
                    new Date(shiftEnd),
                    totalDuration
                  )?.toISOString()
                : shiftStart,
            endDateTime:
              type === SHIFT_ARRIVE_LATE_CODE &&
              isValidDate(new Date(shiftStart)) &&
              isValidDate(
                addMinutes(new Date(shiftStart), totalDuration)
              )
                ? addMinutes(
                    new Date(shiftStart),
                    totalDuration
                  )?.toISOString()
                : shiftEnd,
          };
        });
      }
    } else if (selectedLeaveType2 && type === SHIFT_CANCEL_CODE) {
      // For full shift callouts, the secondary duration is just the remainder
      setDuration2(shiftDurationInMinutes - (duration ?? 0));
      setDurationOptions2(
        [{
          text: getHoursAndMinutesStringFromFloat((shiftDurationInMinutes - (duration ?? 0))/60),
          timeInMinutes: shiftDurationInMinutes - (duration ?? 0),
        }]
      );
      setCallOffDataValue((prev) => ({
        ...prev,
        leaveTypes: [
          {...prev.leaveTypes[0]},
          { leaveTypeId: selectedLeaveType2.id,
            requestedHours: (shiftDurationInMinutes - (duration ?? 0))/60,
          }],
        startDateTime: shiftStart,
        endDateTime: shiftEnd
      }));
    }
  }, [duration, duration2, selectedLeaveType2, shiftEnd, shiftStart, shiftDurationInMinutes, type]);

  useEffect(() => {
    if (isDataInitialized) return;

    if (requestTypes.length > 0) {
      setCallOffDataValue((prev) => ({
        ...prev,
        requestTypeId: requestTypeId || requestTypes[0].id,
      }));
    }
    if (shiftStart && shiftEnd) {
      setCallOffDataValue((prev) => ({
        ...prev,
        startDateTime: shiftStart,
        endDateTime: shiftEnd,
      }));
    }
  }, [requestTypes, shiftStart, shiftEnd, requestTypeId, isDataInitialized]);

  const handleRequestTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newRequestTypeId = Number(e.target.value);
      setRequestTypeId(newRequestTypeId);
      const newType = requestTypes?.find((t) => t.id === newRequestTypeId)?.code.toLowerCase();
      const isNewTypeSC = newType === SHIFT_CANCEL_CODE;

      setCallOffDataValue((prev) => ({
        ...prev,
        requestTypeId: newRequestTypeId,
        // If switching to 'sc', lock duration to full shift.
        // If switching away from 'sc' with full duration selected, reset duration.
        startDateTime: shiftStart,
        endDateTime: isNewTypeSC
            ? shiftEnd
            : shiftStart,
      }));
    },
    [requestTypes, shiftEnd, shiftStart]
  );

  const handleCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCallOffDataValue((prev) => ({
        ...prev,
        supervisorComment: e.target.value,
      }));
    },
    []
  );

  const isDirty = useMemo(() => {
    if (!isDataInitialized || !initialData) return false;

    if (callOffDataValue.requestTypeId !== initialData.requestTypeId) return true;
    const initialComment = initialData.supervisorComment || "";
    if (callOffDataValue.supervisorComment !== initialComment) return true;

    const current = callOffDataValue.leaveTypes;
    const original: { leaveTypeId: number; requestedHours: number }[] = initialData.leaveTypes || [];
    if (current.length !== original.length) return true;
    for (let i = 0; i < current.length; i++) {
      if (current[i].leaveTypeId !== original[i].leaveTypeId) return true;
      if (Math.abs(current[i].requestedHours - original[i].requestedHours) > 0.001) return true;
    }

    return false;
  }, [callOffDataValue, initialData, isDataInitialized]);

  useEffect(() => {
    if (setIsDirty) setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  useEffect(() => {
    const callOffParams = {
      ftRequestTypeId: callOffDataValue.requestTypeId,
      ftLeaveTypes: callOffDataValue.leaveTypes,
      ftSupervisorComment: callOffDataValue.supervisorComment,
      ftStartDateTime: callOffDataValue.startDateTime,
      ftEndDateTime: callOffDataValue.endDateTime,
      ftFromStatus: fromStatus,
    };

    setCalledOffData(callOffParams);
  }, [
    callOffDataValue,
    fromStatus,
    setCalledOffData]);

  // Determine if the pairing accordion should be shown.
  const showPairingAccordion = useMemo(() => {
    // Accordion should be shown only if a first leave type is selected, and it has pairing enabled.
    if (!selectedLeaveType || !selectedLeaveType.pairingEnabled) {
      return false;
    }
    // Also, there must be at least one other leave type with pairing enabled.
    const otherPairingEnabledTypes = workerCallOffs.filter(
      (type) => type.pairingEnabled && type.id !== selectedLeaveType.id
    );
    return otherPairingEnabledTypes.length > 0
      && ((durationOptions?.length > 1 && !selectedLeaveType2)
        || (durationOptions?.length > 0 && selectedLeaveType2));
  }, [durationOptions?.length, selectedLeaveType, workerCallOffs, selectedLeaveType2]);

  // Create a filtered list for the second dropdown.
  const pairedLeaveTypes = useMemo(() => {
    if (!workerCallOffs || !selectedLeaveType) {
      return [];
    }
    // Filter the list to include only other pairing-enabled types
    return workerCallOffs.filter(
      (type) => type.pairingEnabled && type.id !== selectedLeaveType.id
    );
  }, [workerCallOffs, selectedLeaveType]);

  const parseDurationInput = (input: string) => {
    const floatValue = parseFloat(input);
    if (isNaN(floatValue)) return 0;
    return Math.round(floatValue * 60);
  };

  const disabledIndexes = useMemo(() => {
    return pairedLeaveTypes?.map((item, index) => isItemDisabled(item) ? index : -1).filter(index => index !== -1);
  }, [isItemDisabled, pairedLeaveTypes]);

  const primaryDisabledIndexes = useMemo(() => {
    return workerCallOffs?.map((item, index) => isPrimaryItemDisabled(item) ? index : -1).filter(index => index !== -1);
  },[isPrimaryItemDisabled, workerCallOffs]);

  const isLoading = useMemo(
    () =>
      fetchRequestTypesStatus === "pending" ||
      fetchWorkerCallOffsStatus === "pending" || !isDataInitialized,
    [fetchRequestTypesStatus, fetchWorkerCallOffsStatus, isDataInitialized]
  );

  useEffect(() => {
    try {
      if (!isDataInitialized || initialData) return;
      if (requestTypes.length !== 0) {
        const leaveRequest = requestTypes.find(
          rType => (rType.code).toLowerCase() === type);
        if (leaveRequest && leaveRequest.id !== requestTypeId) {
          setRequestTypeId(leaveRequest?.id);
          setSelectedLeaveType(undefined);
          setDurationOptions([]);
          setDuration(0);
          setSelectedLeaveType2(undefined);
          setDurationOptions2([]);
          setDuration2(0);
          setCallOffDataValue(
            prev => ({
              ...prev,
              requestTypeId: leaveRequest.id,
              leaveTypes: [],
              startDateTime: shiftStart,
              endDateTime: shiftEnd,
            })
          )
          lastSelectedLeaveTypeId.current = undefined;
        }
      }
    } catch (e) {
      // Do nothing
    }
  }, [requestTypes, type, initialData, isDataInitialized, requestTypeId, shiftStart, shiftEnd]);

  const handleLeaveTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const foundLeaveType = workerCallOffs.find(
        (type) => type.id === Number(e.target.value)
      );
      setSelectedLeaveType(foundLeaveType ?? undefined);
      // Reset durations and clear secondary leave type when primary changes
      setDuration(0);
      setDuration2(0);
      setSelectedLeaveType2(undefined);
      if (foundLeaveType) {
        setCallOffDataValue((prev) => ({
          ...prev,
          leaveTypes: [
            {
              leaveTypeId: foundLeaveType.id,
              requestedHours: 0,
            },
          ],
        }));
      }
    },
    [workerCallOffs]
  );

  const handleLeaveTypeChangeForSecondTimeOff = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const foundLeaveType = workerCallOffs.find(
        (type) => type.id === Number(e.target.value)
      );
      if (foundLeaveType && selectedLeaveType && foundLeaveType.id === selectedLeaveType.id) {
        setSelectedLeaveType2(undefined);
        setDuration2(0);
        setCallOffDataValue((prev) => ({
          ...prev,
          leaveTypes: prev.leaveTypes.slice(0, 1),
        }));
      } else {
        setSelectedLeaveType2(foundLeaveType ?? undefined);
        
        // When we select selectedLeaveType2 for the first time in Full Shift mode (New Request)
        if (foundLeaveType && type === SHIFT_CANCEL_CODE && !initialData && !selectedLeaveType2) {
          const minSecondaryInMinutes = Math.round(foundLeaveType.incrementHours * 60);
          const calculatedPrimaryDuration = shiftDurationInMinutes - minSecondaryInMinutes;
          
          if (calculatedPrimaryDuration > 0) {
            setDuration(calculatedPrimaryDuration);
            setDuration2(minSecondaryInMinutes);
          }
        }
      }
    },
    [workerCallOffs, selectedLeaveType, type, initialData, selectedLeaveType2, shiftDurationInMinutes]
  );

  if (isLoading) {
    return <MarkCalledOffSkeleton />;
  }
  return (
    <>
      <Box>
        <Box sx={{ padding: "24px 0px 20px 0px" }}>
          {/* Timing Options */}
          <Box sx={{ marginBottom: "32px" }}>
            {fetchRequestTypesStatus === "fulfilled" && (
              <RadioGroup
                value={callOffDataValue.requestTypeId}
                onChange={handleRequestTypeChange}
                sx={{ flexDirection: "row", gap: "24px" }}
              >
                {requestTypes.map((type) => (
                  <FormControlLabel
                    key={type.id}
                    value={type.id}
                    control={<Radio />}
                    label={<Typography>{type.name}</Typography>}
                  />
                ))}
              </RadioGroup>
            )}
          </Box>

          {/* Duration and Time Off Type */}
          <Box sx={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
            <FormControl sx={{ flex: 2 }}>
              <TextField
                select
                label={t("full-time-shifts_time-off-type")}
                value={selectedLeaveType?.id || ""}
                onChange={handleLeaveTypeChange}
                fullWidth
                variant='filled'
                slotProps={{
                  select: {
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: "200px",
                          width: "300px",
                          overflowY: "scroll",
                        },
                      },
                    },
                    renderValue: (value: unknown) => {
                      const callOff = workerCallOffs.find(
                        (callOff) => callOff.id === Number(value)
                      );
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "16px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {callOff?.name}{!callOff?.isNoLimit && ` (${getHoursAndMinutesStringFromFloat(callOff?.availableHrs ?? 0)})`}
                          </Typography>
                        </Box>
                      );
                    },
                  },
                }}
              >
                {workerCallOffs.map((callOff, index) => (
                  <MenuItem
                    key={callOff.id}
                    value={callOff.id}
                    disabled={primaryDisabledIndexes?.includes(index)}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "16px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {callOff.name}{!callOff.isNoLimit && ` (${getHoursAndMinutesStringFromFloat(callOff.availableHrs ?? 0)})`}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "16px",
                        marginLeft: "8px",
                        flexShrink: 0,
                      }}
                    >{`${isPrimaryItemDisabled(callOff) ? ` ${t("full-time-shifts_min-duration", { duration: getHoursAndMinutesStringFromFloat(callOff.incrementHours) })}` : ""}`}
                    </Typography>
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <Autocomplete
                  freeSolo
                  ListboxProps={{ style: { maxHeight: "200px" } }}
                  options={durationOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option?.text || "";
                  }}
                  value={(duration ?? 0) > 0 ? (durationOptions?.find(
                    (option) => Math.round(option.timeInMinutes) === Math.round(duration ?? 0)
                  ) || { text: getHoursAndMinutesStringFromFloat((duration ?? 0)/60), timeInMinutes: duration ?? 0 }) : null}
                  onChange={(e, newValue) => {
                    try {
                      let newDurationValue = 0;
                      if (typeof newValue === "string") {
                        newDurationValue = parseDurationInput(newValue);
                      } else if (newValue && typeof newValue !== "string") {
                        newDurationValue = newValue.timeInMinutes;
                      }
                      setDuration(newDurationValue);
                      
                      // For full shift callouts, the secondary duration is just the remainder
                      if (type === SHIFT_CANCEL_CODE && selectedLeaveType2) {
                        const newDuration2Value = shiftDurationInMinutes - newDurationValue;
                        setDuration2(newDuration2Value);
                      }

                      if (isValidDate(new Date(shiftStart))
                        && isValidDate(new Date(shiftEnd))) {
                        setCallOffDataValue((prev) => {
                          const updatedLeaveTypes = [...prev.leaveTypes];
                          if (updatedLeaveTypes.length > 0) {
                            updatedLeaveTypes[0] = {
                              ...updatedLeaveTypes[0],
                              requestedHours: newDurationValue / 60,
                            };
                          } else if (selectedLeaveType) {
                            updatedLeaveTypes[0] = {
                              leaveTypeId: selectedLeaveType.id,
                              requestedHours: newDurationValue / 60,
                            };
                          }

                          let totalDuration;
                          if (type === SHIFT_CANCEL_CODE && selectedLeaveType2) {
                             const newDuration2Value = shiftDurationInMinutes - newDurationValue;
                             updatedLeaveTypes[1] = {
                               leaveTypeId: selectedLeaveType2.id,
                               requestedHours: newDuration2Value / 60,
                             };
                             totalDuration = shiftDurationInMinutes;
                          } else {
                             totalDuration = (newDurationValue ?? 0) + (updatedLeaveTypes[1]?.requestedHours * 60 ?? 0);
                          }

                          return {
                            ...prev,
                            leaveTypes: updatedLeaveTypes,
                            startDateTime:
                              type === SHIFT_LEAVE_EARLY_CODE &&
                              isValidDate(new Date(shiftEnd)) &&
                              isValidDate(
                                subMinutes(
                                  new Date(shiftEnd),
                                  totalDuration
                                )
                              )
                                ? subMinutes(
                                    new Date(shiftEnd),
                                    totalDuration
                                  )?.toISOString()
                                : shiftStart,
                            endDateTime:
                              type === SHIFT_ARRIVE_LATE_CODE &&
                              isValidDate(
                                new Date(shiftStart)
                              ) &&
                              isValidDate(
                                addMinutes(
                                  new Date(shiftStart),
                                  totalDuration
                                )
                              )
                                ? addMinutes(
                                    new Date(shiftStart),
                                    totalDuration
                                  )?.toISOString()
                                : shiftEnd,
                          };
                        });
                      }
                    } catch (e) {
                      console.log("Error: ", e);
                    }
                  }}
                  disabled={
                    (callOffDataValue?.leaveTypes?.length < 2
                    && type === SHIFT_CANCEL_CODE) ||
                    durationOptions?.length === 0
                  }
                  noOptionsText={t("full-time-shifts_no-options-available")}
                  renderInput={(params) => (
                      <TextField
                          {...params}
                          label={t("full-time-shifts_duration")}
                          variant="filled"
                          fullWidth
                      />
                  )}
              />
          </FormControl>
          </Box>

          {showPairingAccordion && (
            <Box sx={{
              backgroundColor: colors.grey,
              borderRadius: "4px",
              marginBottom: "16px",
            }}>
              <Box sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                paddingLeft: "12px",
                paddingRight: "8px",
                paddingTop: "16px",
                paddingBottom: "16px",
                cursor: "pointer",
              }}
                   onClick={() => {
                setIsPairingAccordionOpen(!isPairingAccordionOpen);
                if (isPairingAccordionOpen) {
                  setSelectedLeaveType2(undefined);
                  setDuration2(null);
                  setDurationOptions2([]);
                }
              }}
              >
                <Typography sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: 400,
                  color: colors.title,
                }}>
                  {t("full-time-shifts_add-second-time-off-type")}
                </Typography>
                {isPairingAccordionOpen ? (
                  <RemoveOutlined sx={{
                    color: colors.title,
                    fontSize: "20px",
                  }} />
                ) : (
                  <AddOutlined sx={{
                    color: colors.title,
                    fontSize: "20px",
                  }} />
                )}
              </Box>
              {isPairingAccordionOpen && (
                <Box sx={{ display: "flex",
                  gap: "24px",
                  padding: "12px",
                  backgroundColor: colors.greyLight,
                  }}>
                  <FormControl sx={{ flex: 2 }}>
                    <TextField
                      select
                      label={t("full-time-shifts_time-off-type")}
                      value={selectedLeaveType2?.id || ""}
                      onChange={handleLeaveTypeChangeForSecondTimeOff}
                      fullWidth
                      variant='filled'
                      slotProps={{
                        select: {
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: "200px",
                                width: "300px",
                                overflowY: "scroll",
                              },
                            },
                          },
                          renderValue: (value: unknown) => {
                            const callOff = pairedLeaveTypes.find(
                              (callOff) => callOff.id === Number(value)
                            );
                            if (!callOff) return null;
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: "Roboto",
                                    fontWeight: 400,
                                    fontSize: "16px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {callOff?.name}{!callOff?.isNoLimit && ` (${getHoursAndMinutesStringFromFloat(callOff?.availableHrs ?? 0)})`}
                                </Typography>
                              </Box>
                            );
                          },
                        },
                      }}
                    >
                      {pairedLeaveTypes?.map((callOff, index) => (
                        <MenuItem
                          key={callOff?.id}
                          value={callOff?.id}
                          disabled={disabledIndexes?.includes(index)}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "16px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {callOff?.name}{!callOff?.isNoLimit && ` (${getHoursAndMinutesStringFromFloat(callOff?.availableHrs ?? 0)})`}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "16px",
                              marginLeft: "8px",
                              flexShrink: 0,
                            }}
                          >{`${isItemDisabled(callOff) ? ` ${t("full-time-shifts_min-duration", { duration: getHoursAndMinutesStringFromFloat(callOff?.incrementHours) })}` : ""}`}
                          </Typography>
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <Autocomplete
                      freeSolo
                      ListboxProps={{ style: { maxHeight: "200px" } }}
                      options={durationOptions2}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return option?.text || "";
                      }}
                      value={(duration2 ?? 0) > 0 ? (durationOptions2.find(
                        (option) => Math.round(option.timeInMinutes) === Math.round(duration2 ?? 0)
                      ) || { text: getHoursAndMinutesStringFromFloat((duration2 ?? 0)/60), timeInMinutes: duration2 ?? 0 }) : null}
                      onChange={(e, newValue) => {
                        try {
                          let newDuration2Value = 0;
                          if (typeof newValue === "string") {
                            newDuration2Value = parseDurationInput(newValue);
                          } else if (newValue && typeof newValue !== "string") {
                            newDuration2Value = newValue.timeInMinutes;
                          }
                          setDuration2(newDuration2Value);
                          
                          // For full shift callouts, the primary duration is just the remainder
                          if (type === SHIFT_CANCEL_CODE && selectedLeaveType) {
                            const newPrimaryDuration = shiftDurationInMinutes - newDuration2Value;
                            setDuration(newPrimaryDuration);
                          }

                          if (isValidDate(new Date(shiftStart))
                          && isValidDate(new Date(shiftEnd))
                          && selectedLeaveType2) {
                            setCallOffDataValue(
                              prevState => {
                                const updatedLeaveTypes = [...prevState.leaveTypes];
                                
                                let totalDuration;
                                if (type === SHIFT_CANCEL_CODE && selectedLeaveType) {
                                  const newPrimaryDuration = shiftDurationInMinutes - newDuration2Value;
                                  updatedLeaveTypes[0] = {
                                    ...updatedLeaveTypes[0],
                                    requestedHours: newPrimaryDuration / 60
                                  };
                                  updatedLeaveTypes[1] = {
                                    leaveTypeId: selectedLeaveType2.id,
                                    requestedHours: newDuration2Value / 60
                                  };
                                  totalDuration = shiftDurationInMinutes;
                                } else {
                                  if (updatedLeaveTypes.length > 1) {
                                    updatedLeaveTypes[1] = {
                                      ...updatedLeaveTypes[1],
                                      requestedHours: newDuration2Value / 60
                                    };
                                  } else {
                                    updatedLeaveTypes[1] = {
                                      leaveTypeId: selectedLeaveType2.id,
                                      requestedHours: newDuration2Value / 60
                                    };
                                  }
                                  totalDuration = (updatedLeaveTypes[0]?.requestedHours * 60 ?? 0) + (newDuration2Value ?? 0);
                                }

                                return {
                                  ...prevState,
                                  leaveTypes: updatedLeaveTypes,
                                  startDateTime:
                                    type === SHIFT_LEAVE_EARLY_CODE &&
                                    isValidDate(
                                      new Date(shiftEnd)
                                    ) &&
                                    isValidDate(
                                      subMinutes(
                                        new Date(shiftEnd),
                                        totalDuration
                                      )
                                    )
                                      ? subMinutes(
                                          new Date(shiftEnd),
                                          totalDuration
                                        )?.toISOString()
                                      : shiftStart,
                                  endDateTime:
                                    type === SHIFT_ARRIVE_LATE_CODE &&
                                    isValidDate(
                                      new Date(shiftStart)
                                    ) &&
                                    isValidDate(
                                      addMinutes(
                                        new Date(shiftStart),
                                        totalDuration
                                      )
                                    )
                                      ? addMinutes(
                                          new Date(shiftStart),
                                          totalDuration
                                        )?.toISOString()
                                      : shiftEnd,
                                };
                              }
                            )
                          }
                        } catch (e) {
                          console.log("Error2: ", e);
                        }
                      }}
                      disabled={
                        durationOptions2?.length === 0
                      }
                      noOptionsText={t("full-time-shifts_no-options-available")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("full-time-shifts_duration")}
                          variant="filled"
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Box>
              )}
            </Box>
          )}
          {((selectedLeaveType && !selectedLeaveType.isNoLimit && (selectedLeaveType.availableHrs ?? 0) < (duration ?? 0)/60)
            || (selectedLeaveType2 && !selectedLeaveType2.isNoLimit && (selectedLeaveType2.availableHrs ?? 0) < (duration2 ?? 0)/60))
            && (
            <Box
              sx={{
                marginBottom: "16px",
                marginTop: "16px",
              }}
            >
              <Alert severity='warning'>
                {t("full-time-shifts_time-off-balance-insufficient")}
              </Alert>
            </Box>
          )}

          {/* Reason for Absence */}
          <Box sx={{ marginBottom: "16px", position: "relative", marginTop: "16px" }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={callOffDataValue.supervisorComment}
              onChange={handleCommentChange}
              placeholder={t("full-time-shifts_reason-for-absence")}
              variant="filled"
              inputProps={{ maxLength: 50 }}
              sx={{
                "& .MuiFilledInput-root": {
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "16px",
                  backgroundColor: "#FAFAFA",
                },
              }}
            />
            {/* Character count outside the bottom right of the TextField */}
            <Box
              sx={{
                position: "absolute",
                bottom: "-20px",
                right: 0,
                fontSize: "12px",
                color: "rgba(0, 0, 0, 0.6)",
                fontFamily: "Roboto",
              }}
            >
              {callOffDataValue?.supervisorComment?.length} / 50
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default MarkCalledOff;